import { readClock as parseClock, RESERVED_KEYS } from '@tahi/core';
import { getTableColumns } from 'drizzle-orm';
import type { SQLiteTable } from 'drizzle-orm/sqlite-core';

export type Row = Record<string, unknown>;

/** Set by the applier, never taken from mutation values (conventions §13: RESERVED_KEYS). */
const RESERVED = new Set<string>(RESERVED_KEYS);

function columns(table: SQLiteTable): Record<string, { columnType: string }> {
  return getTableColumns(table) as unknown as Record<string, { columnType: string }>;
}

/** Mutation values that are real, non-reserved columns of `table`. */
export function pickColumns(table: SQLiteTable, values: Row): Row {
  const cols = columns(table);
  return Object.fromEntries(
    Object.entries(values).filter(([k, v]) => k in cols && !RESERVED.has(k) && v !== undefined),
  );
}

/** Drizzle serialises json-mode columns itself; plain TEXT columns holding JSON need a string. */
export function encodeRow(table: SQLiteTable, row: Row): Row {
  const cols = columns(table);
  const out: Row = {};
  for (const [k, v] of Object.entries(row)) {
    const col = cols[k];
    if (!col || v === undefined) continue;
    const isJson = col.columnType.endsWith('Json');
    out[k] = v !== null && typeof v === 'object' && !isJson ? JSON.stringify(v) : v;
  }
  return out;
}

/** The row's clock as `{ field: clockKey(at, mutationId) }` (core's readClock, shared with the Worker). */
export function readClock(row: Row | null | undefined): Record<string, string> {
  return parseClock(row?.clock);
}
